import i18next, { type Resource } from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import detectLanguage from 'i18next-browser-languagedetector';
import { ReactNode } from 'react';

import type { Namespace } from './namespaces';
export * from './namespaces';

/**
 * Cobines multiple namespaces into a single resource object for use in i18next.
 * @param namespaces The namespaces to combine.
 */
const namespacesToResources = (namespaces: Namespace[]): { resources: Resource; ns: string[] } => {
    const modules = namespaces.reduce((acc, m) => ({ ...acc, ...m }), {});

    const resources: Resource = {};
    const ns: Set<string> = new Set();

    for (const path in modules) {
        // The regex path must match the path to the namespace files used in the `./namespaces.ts` file.
        const [, locale, namespace] = path.match(/\.\/locales\/(.*)\/(.*)\.json$/) || [];
        if (locale && namespace) {
            if (!resources[locale]) {
                resources[locale] = {};
            }
            resources[locale][namespace] = modules[path];
            ns.add(namespace);
        }
    }
    return { resources, ns: [...ns] };
};

/**
 * Create a new i18next instance with access to the given namespaces.
 * Only the given namespaces will be available in the i18next instance.
 * @param namespaces Namespaces to include in the i18next instance.
 * @returns A new i18next instance.
 */
export const createI18n = (...namespaces: Namespace[]): typeof i18next => {
    const i18nInstance = i18next.createInstance();
    const { resources, ns } = namespacesToResources(namespaces);

    i18nInstance
        .use(detectLanguage)
        .use(initReactI18next)
        .init({
            resources,
            ns,
            fallbackLng: 'no',
            detection: {
                order: ['htmlTag'],
            },
            debug: false,
            interpolation: {
                escapeValue: false,
            },
        });
    return i18nInstance;
};

export type I18nProviderProps = {
    children: ReactNode;
    /**
     * An i18next instance to use.
     * Create it using `createI18n` and apply the namespaces that should be available.
     */
    i18n: typeof i18next;
};

export const I18nProvider = ({ children, i18n }: I18nProviderProps) => {
    return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};
